<template>
  <b-card>
    <h3 class="mb-4">Expense Type List</h3>

    <!-- Loader -->
    <div v-if="loading" class="text-center mt-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <!-- Search + Export -->
    <div class="col-12 mt-16">
      <b-row class="align-items-center">
        <!-- Search Filter -->
        <b-col lg="6" class="my-1">
          <b-form-group label="" label-for="filter-input" label-cols-sm="1" label-align-sm="right" label-size="sm" class="mb-0">
            <b-input-group size="sm">
              <b-form-input id="filter-input" v-model="filter" type="search" placeholder="Search Expense Type..."></b-form-input>
              <b-input-group-append>
                <b-button @click="applyFilter" :disabled="!filter" variant="primary">Apply</b-button>
                <b-button :disabled="!filter" @click="clearFilter">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Export -->
        <b-col lg="6" class="my-1 d-flex justify-content-end">
          <b-button @click="exportAllDataToCSV" variant="primary">Export</b-button>
          <b-button  v-b-tooltip.hover title="Add Expense Types"  @click="addExpenseType" variant="primary" class="ml-2">Add Expense Type</b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Expense Type Table -->
    <b-row>
      <div class="col-12 mt-16">
        <b-skeleton-table
                    v-if="loading"
                    :rows="perPage"
                    :columns="fields.length"
                    animation="wave"
                    bordered
                    class="mb-3"
                    />
        <b-table v-else
          id="dataTable"
          :items="expenseTypes"
          :busy.sync="loading"
          :fields="fields"
          :current-page="currentPage"
          :per-page="perPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive
          show-empty
          striped hover  class="custom-zebra"
        >
          <!-- Custom Columns -->
          <template #cell(name)="row">
            <span>{{ row.item.name || 'N/A' }}</span>
          </template>
          <!-- Actions -->
          <template #cell(actions)="row">
            <!-- Delete -->
            <b-button  v-b-tooltip.hover title="Delete Expense Type"  @click="showDeleteConfirmation(row.item.id)" variant="link" class="p-0 btn-sm mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                style="color: red; margin-left: 6px; margin-bottom: 10px" class="bi bi-eye"
                viewBox="0 0 16 16">
                <path
                  d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
              </svg>
            </b-button>
            <!-- Edit -->
            <b-button  v-b-tooltip.hover title="Edit Expense Type"  @click="editExpenseType(row.item.id)" variant="link" class="p-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                style="color: orange; margin-left: 10px; margin-bottom: 10px" class="bi bi-pencil"
                viewBox="0 0 16 16">
                <path
                  d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
              </svg>
            </b-button>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="mx-8 d-flex justify-content-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            aria-controls="dataTable"
            @change="pageChanged"
          ></b-pagination>
        </div>
      </div>
    </b-row>

    <!-- Delete Confirmation Modal -->
    <b-modal v-model="showDeleteConfirmations" title="Delete Confirmation">
      <p>Are you sure you want to delete this Expense Type?</p>
      <template #modal-footer>
        <b-button variant="danger" @click="deleteItem(itemIdToDelete)">Delete</b-button>
        <b-button variant="secondary" @click="showDeleteConfirmations = false">Cancel</b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BTable,
  BFormGroup,
  BInputGroup,
  BFormInput,
  BPagination,
  BInputGroupAppend,
  BSpinner,
  BModal,
  BSkeleton,
  BSkeletonTable,
} from "bootstrap-vue";
import axios from "../../../axios";
import Papa from "papaparse";

export default {
  data() {
    return {
      filter: "",
      total: 0,
      perPage: 10,
      currentPage: 1,
      expenseTypes: [],
      sortBy: "",
      sortDesc: false,
      loading: false,
      showDeleteConfirmations: false,
      itemIdToDelete: null,
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Name", sortable: true },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BPagination,
    BInputGroupAppend,
    BSpinner,
    BModal,
    BSkeleton,
    BSkeletonTable,
  },
  mounted() {
    this.fetchExpenseTypes();
  },
  methods: {
 
    async fetchExpenseTypes() {
  this.loading = true;
  try {
    const response = await axios.post("expenseTypes", {
      search: this.filter,
      page: this.currentPage,
      per_page: this.perPage,
    });

    const pagination = response.data.data;
    this.expenseTypes = Array.isArray(pagination.data) ? pagination.data : [];
    this.total = pagination.total || 0;
    this.currentPage = pagination.current_page || 1;
  } catch (error) {
    this.expenseTypes = [];
  } finally {
    this.loading = false;
  }
},

    pageChanged(newPage) {
      this.currentPage = newPage;
      this.fetchExpenseTypes();
    },
    applyFilter() {
      this.currentPage = 1;
      this.fetchExpenseTypes();
    },
    clearFilter() {
      this.filter = "";
      this.currentPage = 1;
      this.fetchExpenseTypes();
    },
    addExpenseType() {
      this.$router.push({ name: "AddExpenseTypes" });
    },
    editExpenseType(id) {
      this.$router.push({ name: "EditExpenseTypes", params: { id } });
    },
    showDeleteConfirmation(id) {
      this.itemIdToDelete = id;
      this.showDeleteConfirmations = true;
    },
    async deleteItem(id) {
      try {
        await axios.delete(`expenseTypesDelete/${id}`);
        this.showDeleteConfirmations = false;
        this.fetchExpenseTypes();
      } catch (error) {
        // handle error
      }
    },
    exportAllDataToCSV() {
      this.fetchAllExpenseTypes((allTypes) => {
        const csv = Papa.unparse(allTypes);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", "expense_types.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    fetchAllExpenseTypes(callback) {
      axios
        .get("expenseTypesForExport", { params: { per_page: this.total } })
        .then((response) => callback(response.data.data))
        .catch(() => {});
    },
  },
};
</script>
  <style>
  /* Zebra striping */
  .custom-zebra tbody tr:nth-of-type(odd) {
    background-color: #f0f2f5; /* darker than default */
  }

  /* Hover effect */
  .custom-zebra tbody tr:hover {
    background-color: #e0e4ea !important; /* slightly darker hover */
  }
  </style>